import React, { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { authService } from '../services/authService';
import { openCustomerPortal } from '../services/stripeBillingService';
import { useNavigate } from 'react-router-dom';
import { Navigation } from './Navigation';
import EngineKeySettings from './risk/EngineKeySettings';
import ExperienceAndRisk from './settings/ExperienceAndRisk';

export const Profile: React.FC = () => {
  const { user, token, logout, updateProfile } = useAuth();
  const navigate = useNavigate();
  const [isChangingPassword, setIsChangingPassword] = useState(false);
  const [isTogglingTwoFactor, setIsTogglingTwoFactor] = useState(false);
  const [passwordForm, setPasswordForm] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [riskTolerance, setRiskTolerance] = useState(user?.riskTolerance || 'all');
  const [isUpdatingRisk, setIsUpdatingRisk] = useState(false);
  const [isUpdatingSubscriptions, setIsUpdatingSubscriptions] = useState(false);
  const [subscriptionForm, setSubscriptionForm] = useState({
    emailNotches: (user as any)?.emailNotchesEnabled || false,
    disclaimerAccepted: user?.disclaimerAccepted || false
  });
  const [phoneInput, setPhoneInput] = useState('');
  const [otpInput, setOtpInput] = useState('');
  const [phoneStep, setPhoneStep] = useState<'idle' | 'otp_sent' | 'verified'>(
    user?.phoneVerified ? 'verified' : 'idle'
  );
  const [isSendingOtp, setIsSendingOtp] = useState(false);
  const [isVerifyingOtp, setIsVerifyingOtp] = useState(false);
  const [smsPrefs, setSmsPrefs] = useState({
    smsStockSignals: user?.smsStockSignals || false,
    smsSpxSignals: user?.smsSpxSignals || false,
    smsSpySignals: (user as any)?.smsSpySignals || false,
    smsEsSignals: (user as any)?.smsEsSignals || false,
    smsMesSignals: (user as any)?.smsMesSignals || false,
    smsOptionsTrades: (user as any)?.smsOptionsTrades || false,
    smsNotchesEnabled: (user as any)?.smsNotchesEnabled || false,
  });
  const [isUpdatingSms, setIsUpdatingSms] = useState(false);
  // TastyTrade state
  const [ttRefreshToken, setTtRefreshToken] = useState('');
  const [ttClientSecret, setTtClientSecret] = useState('');
  const [isConnectingTT, setIsConnectingTT] = useState(false);
  const [isDisconnectingTT, setIsDisconnectingTT] = useState(false);
  const [isLoadingTTAccount, setIsLoadingTTAccount] = useState(false);
  const [ttAccountData, setTtAccountData] = useState<any>(null);
  const [showTTSetup, setShowTTSetup] = useState(false);
  const [showAddConnection, setShowAddConnection] = useState(false);
  const [ttAccounts, setTtAccounts] = useState<any[]>([]);
  const [ttSelectedAccount, setTtSelectedAccount] = useState('');
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isCancelling, setIsCancelling] = useState(false);
  const [isOpeningPortal, setIsOpeningPortal] = useState(false);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [cancelScenario, setCancelScenario] = useState<'refund' | 'no-refund' | 'standard' | null>(null);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const handlePasswordChange = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setMessage(null);

    if (passwordForm.newPassword !== passwordForm.confirmPassword) {
      setError('New passwords do not match');
      return;
    }

    if (passwordForm.newPassword.length < 8) {
      setError('New password must be at least 8 characters');
      return;
    }

    try {
      await authService.changePassword(
        passwordForm.currentPassword,
        passwordForm.newPassword,
        token!
      );
      setMessage('Password changed successfully');
      setPasswordForm({ currentPassword: '', newPassword: '', confirmPassword: '' });
      setIsChangingPassword(false);
    } catch (error: any) {
      setError(error.response?.data?.error || 'Failed to change password');
    }
  };

  const handleSubscriptionUpdate = async () => {
    setError(null);
    setMessage(null);

    // Check if disclaimer is accepted if subscribing to anything
    if (subscriptionForm.emailNotches && !subscriptionForm.disclaimerAccepted) {
      setError('You must accept the disclaimer to receive email alerts');
      return;
    }

    setIsUpdatingSubscriptions(true);

    try {
      // Hourly IV alerts, daily AI report and stock daily briefing emails were retired 2026-10-12: always save them off.
      await authService.updateSubscriptions(
        false,
        false,
        false,
        subscriptionForm.disclaimerAccepted,
        token!
      );

      await updateProfile({
        hourlyEmailsEnabled: false,
        dailyReportEnabled: false,
        stockDailyReportEnabled: false,
        emailNotchesEnabled: subscriptionForm.emailNotches,
        disclaimerAccepted: subscriptionForm.disclaimerAccepted
      } as any);

      setMessage('Subscription preferences updated successfully');
    } catch (error: any) {
      setError(error.response?.data?.error || 'Failed to update subscriptions');
    } finally {
      setIsUpdatingSubscriptions(false);
    }
  };

  if (!user) {
    return <div>Loading...</div>;
  }

  return (
    <div className="profile-container">
      <Navigation />

      <div className="profile-content" style={{ maxWidth: '600px', margin: '2rem auto', padding: '2rem' }}>
        <h1 style={{ color: '#ffffff' }}>Profile</h1>

      {/* User Info */}
      <div className="user-info" style={{ marginBottom: '2rem', padding: '1rem', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '8px', background: 'rgba(255,255,255,0.05)' }}>
        <h2 style={{ color: '#ffffff' }}>Account Information</h2>
        <p style={{ color: '#94a3b8' }}><strong style={{ color: '#cbd5e1' }}>Email:</strong> {user.email}</p>
        <p style={{ color: '#94a3b8' }}><strong style={{ color: '#cbd5e1' }}>Email Verified:</strong> {user.emailVerified ? '✅ Verified' : '❌ Not Verified'}</p>
        <p style={{ color: '#94a3b8' }}><strong style={{ color: '#cbd5e1' }}>Subscription Tier:</strong> <span style={{ textTransform: 'capitalize', fontWeight: 'bold', color: user.effectiveTier === 'free' ? '#64748b' : '#60a5fa' }}>{user.effectiveTier || 'Free'}</span></p>
        <p style={{ color: '#94a3b8' }}><strong style={{ color: '#cbd5e1' }}>Member Since:</strong> {new Date(user.createdAt).toLocaleDateString()}</p>
      </div>

      {/* Subscription Billing Management */}
      {user.effectiveTier && user.effectiveTier !== 'free' && (
        <div className="billing-management" style={{ marginBottom: '2rem', padding: '1rem', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '8px', background: 'rgba(255,255,255,0.05)' }}>
          <h2 style={{ color: '#ffffff' }}>Subscription Billing</h2>

          {/* Stripe customers — single self-service portal button.
              Replaces the legacy PayPal cancel/refund UI below for any user
              with a stripeCustomerId. */}
          {(user as any)?.stripeCustomerId && (
            <div style={{ marginBottom: '1.25rem' }}>
              <p style={{ color: '#94a3b8', fontSize: '0.9rem', marginBottom: '0.75rem' }}>
                Update your card, view invoices, or cancel your subscription through your secure Stripe portal.
              </p>
              <button
                onClick={async () => {
                  if (!token) return;
                  setIsOpeningPortal(true);
                  setError(null);
                  try {
                    const { url } = await openCustomerPortal(token);
                    window.location.assign(url);
                  } catch (err: any) {
                    setError(err?.message || 'Could not open billing portal. Please try again.');
                    setIsOpeningPortal(false);
                  }
                }}
                disabled={isOpeningPortal}
                style={{
                  padding: '0.65rem 1.25rem',
                  backgroundColor: isOpeningPortal ? '#475569' : '#3b82f6',
                  color: 'white', border: 'none', borderRadius: '6px',
                  fontWeight: 600, fontSize: '0.95rem',
                  cursor: isOpeningPortal ? 'wait' : 'pointer',
                }}
              >
                {isOpeningPortal ? 'Opening portal…' : 'Manage Subscription'}
              </button>
              {user.subscriptionStatus === 'cancelling' && user.subscriptionAccessUntil && (
                <p style={{ color: '#fbbf24', marginTop: '0.75rem', fontSize: '0.85rem' }}>
                  Your subscription is set to cancel on{' '}
                  <strong>{new Date(user.subscriptionAccessUntil).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}</strong>.
                  You'll keep access until then.
                </p>
              )}
            </div>
          )}

          {/* Trial Status Bar */}
          {user.isInTrialPeriod && (
            <div style={{
              padding: '1rem', marginBottom: '1rem',
              backgroundColor: 'rgba(59,130,246,0.15)', border: '1px solid rgba(59,130,246,0.3)',
              borderRadius: '8px', color: '#93c5fd'
            }}>
              <strong>60-Day Trial Active</strong>
              <span style={{ marginLeft: '0.5rem' }}>
                — {user.daysRemainingInTrial} day{user.daysRemainingInTrial !== 1 ? 's' : ''} remaining
              </span>
              {user.trialEndDate && (
                <span style={{ display: 'block', fontSize: '0.85rem', marginTop: '0.25rem', color: '#60a5fa' }}>
                  Access through {new Date(user.trialEndDate).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
                </span>
              )}
            </div>
          )}

          {/* Intro pricing note */}
          {user.introDiscountEndsAt && (
            <p style={{ color: '#94a3b8', marginBottom: '1rem', fontSize: '0.9rem' }}>
              Introductory rate of <strong style={{ color: '#60a5fa' }}>$19.99/mo</strong> until{' '}
              {new Date(user.introDiscountEndsAt).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })},
              then $29.99/mo.
            </p>
          )}

          {/* Cancelled with access note */}
          {user.subscriptionStatus === 'cancelled' && user.subscriptionAccessUntil && (
            <div style={{
              padding: '1rem', marginBottom: '1rem',
              backgroundColor: 'rgba(251,191,36,0.15)', border: '1px solid rgba(251,191,36,0.3)',
              borderRadius: '8px', color: '#fbbf24', fontSize: '0.9rem'
            }}>
              Your subscription has been cancelled. You have access until{' '}
              <strong>{new Date(user.subscriptionAccessUntil).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}</strong>.
              No further charges will be made.
            </div>
          )}

          {/* Cancellation Buttons — PayPal customers only.
              Stripe customers manage cancel/refund via the portal block above. */}
          {user.subscriptionStatus !== 'cancelled' && !(user as any)?.stripeCustomerId && (
            <div style={{ marginTop: '1rem' }}>
              {/* Before 7-day minimum */}
              {user.daysUntilRefundEligible !== undefined && user.daysUntilRefundEligible > 0 && (
                <p style={{ color: '#94a3b8', fontSize: '0.9rem', marginBottom: '1rem' }}>
                  Refund eligible in <strong style={{ color: '#fbbf24' }}>{user.daysUntilRefundEligible} day{user.daysUntilRefundEligible !== 1 ? 's' : ''}</strong>
                  {user.refundEligibleAfter && (
                    <span> (after {new Date(user.refundEligibleAfter).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })})</span>
                  )}
                </p>
              )}

              {/* Refund button: within 7-30 day window */}
              {user.canRequestRefund && (
                <button
                  onClick={() => { setCancelScenario('refund'); setShowCancelModal(true); }}
                  style={{
                    padding: '0.75rem 1.5rem', backgroundColor: 'rgba(34,197,94,0.15)',
                    color: '#4ade80', border: '1px solid rgba(34,197,94,0.3)', borderRadius: '4px',
                    fontWeight: 'bold', cursor: 'pointer', marginRight: '1rem'
                  }}
                >
                  Cancel & Get Full Refund
                </button>
              )}

              {/* Post-30-day cancel (within 60-day trial, past refund window) */}
              {user.isInTrialPeriod && !user.canRequestRefund && (user.daysUntilRefundEligible === undefined || user.daysUntilRefundEligible === 0) && (
                <button
                  onClick={() => { setCancelScenario('no-refund'); setShowCancelModal(true); }}
                  style={{
                    padding: '0.75rem 1.5rem', backgroundColor: 'rgba(239,68,68,0.15)',
                    color: '#f87171', border: '1px solid rgba(239,68,68,0.3)', borderRadius: '4px',
                    fontWeight: 'bold', cursor: 'pointer'
                  }}
                >
                  Cancel Subscription
                </button>
              )}

              {/* Standard cancel (after trial period) */}
              {!user.isInTrialPeriod && (
                <button
                  onClick={() => { setCancelScenario('standard'); setShowCancelModal(true); }}
                  style={{
                    padding: '0.75rem 1.5rem', backgroundColor: 'rgba(239,68,68,0.15)',
                    color: '#f87171', border: '1px solid rgba(239,68,68,0.3)', borderRadius: '4px',
                    fontWeight: 'bold', cursor: 'pointer'
                  }}
                >
                  Cancel Subscription
                </button>
              )}

              {user.canRequestRefund && user.daysRemainingForRefund !== undefined && (
                <p style={{ marginTop: '0.75rem', fontSize: '0.8rem', color: '#64748b' }}>
                  Refund window closes in {user.daysRemainingForRefund} day{user.daysRemainingForRefund !== 1 ? 's' : ''}
                </p>
              )}
            </div>
          )}
        </div>
      )}

      {/* Cancellation Confirmation Modal */}
      {showCancelModal && (
        <div style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.7)', display: 'flex', alignItems: 'center',
          justifyContent: 'center', zIndex: 9999
        }}>
          <div style={{
            backgroundColor: '#1e293b', borderRadius: '12px', padding: '2rem',
            maxWidth: '480px', width: '90%', border: '1px solid rgba(255,255,255,0.1)'
          }}>
            <h3 style={{ color: '#ffffff', marginBottom: '1rem' }}>
              {cancelScenario === 'refund' ? 'Cancel & Refund' : 'Cancel Subscription'}
            </h3>

            {cancelScenario === 'refund' && (
              <p style={{ color: '#94a3b8', lineHeight: '1.6', marginBottom: '1.5rem' }}>
                Your subscription will be cancelled immediately and a full refund of <strong style={{ color: '#4ade80' }}>${user.refundProcessed ? '0.00' : '19.99'}</strong> will be issued.
                It may take 3-5 business days to appear. You will be downgraded to the Free tier.
              </p>
            )}

            {cancelScenario === 'no-refund' && (
              <p style={{ color: '#94a3b8', lineHeight: '1.6', marginBottom: '1.5rem' }}>
                Your refund window has passed. Your subscription will be cancelled and <strong style={{ color: '#fbbf24' }}>no refund will be issued</strong>.
                You'll keep access until{' '}
                <strong>{user.trialEndDate ? new Date(user.trialEndDate).toLocaleDateString() : 'the end of your trial period'}</strong>.
                No future charges will be made.
              </p>
            )}

            {cancelScenario === 'standard' && (
              <p style={{ color: '#94a3b8', lineHeight: '1.6', marginBottom: '1.5rem' }}>
                Your subscription will be cancelled. You'll retain access until the end of your current billing period.
                No further charges will be made.
              </p>
            )}

            <div style={{ display: 'flex', gap: '1rem', justifyContent: 'flex-end' }}>
              <button
                onClick={() => { setShowCancelModal(false); setCancelScenario(null); }}
                disabled={isCancelling}
                style={{
                  padding: '0.5rem 1.25rem', backgroundColor: 'rgba(255,255,255,0.1)',
                  color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer'
                }}
              >
                Keep Subscription
              </button>
              <button
                onClick={async () => {
                  setIsCancelling(true);
                  setError(null);
                  setMessage(null);
                  try {
                    const result = await authService.cancelSubscription(token!);
                    setMessage(result.message);
                    setShowCancelModal(false);
                    setCancelScenario(null);
                    setTimeout(() => window.location.reload(), 2000);
                  } catch (err: any) {
                    setError(err.response?.data?.error || err.message || 'Failed to cancel subscription');
                    setShowCancelModal(false);
                    setCancelScenario(null);
                  } finally {
                    setIsCancelling(false);
                  }
                }}
                disabled={isCancelling}
                style={{
                  padding: '0.5rem 1.25rem',
                  backgroundColor: cancelScenario === 'refund' ? '#22c55e' : '#ef4444',
                  color: 'white', border: 'none', borderRadius: '4px',
                  cursor: isCancelling ? 'not-allowed' : 'pointer', fontWeight: 'bold'
                }}
              >
                {isCancelling ? 'Processing...' : cancelScenario === 'refund' ? 'Confirm Refund & Cancel' : 'Confirm Cancellation'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Options Research Preferences */}
      <div className="risk-tolerance-section" style={{ marginBottom: '2rem', padding: '1rem', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '8px', background: 'rgba(255,255,255,0.05)' }}>
        <h2 style={{ color: '#ffffff' }}>Options Research Preferences</h2>
        <p style={{ color: '#94a3b8', fontSize: '0.9em', marginBottom: '1rem' }}>
          Filter options research based on your risk tolerance. This controls which picks appear on your Options Research page.
        </p>

        <div style={{ marginBottom: '1rem' }}>
          <label style={{ display: 'block', color: '#cbd5e1', marginBottom: '0.5rem', fontWeight: 'bold' }}>Risk Tolerance</label>
          <select
            value={riskTolerance}
            onChange={(e) => setRiskTolerance(e.target.value)}
            style={{
              width: '100%',
              padding: '0.5rem',
              border: '1px solid rgba(255,255,255,0.2)',
              borderRadius: '4px',
              background: 'rgba(255,255,255,0.1)',
              color: '#ffffff',
              fontSize: '1em',
              cursor: 'pointer'
            }}
          >
            <option value="all">All Research (No Filter)</option>
            <option value="conservative">Conservative Only</option>
            <option value="moderate">Conservative + Moderate</option>
            <option value="aggressive">Conservative + Moderate + Aggressive</option>
          </select>
        </div>

        <div style={{ fontSize: '0.85em', color: '#64748b', marginBottom: '1rem', lineHeight: '1.6' }}>
          {riskTolerance === 'all' && 'Shows all research regardless of risk profile, including high-risk strategies.'}
          {riskTolerance === 'conservative' && 'Shows only conservative strategies like high-POP short puts and covered calls. Lower risk, lower reward.'}
          {riskTolerance === 'moderate' && 'Shows conservative and moderate strategies. Balanced approach with standard position sizing.'}
          {riskTolerance === 'aggressive' && 'Shows conservative, moderate, and aggressive strategies. Excludes only high-risk earnings plays.'}
        </div>

        <button
          onClick={async () => {
            setError(null);
            setMessage(null);
            setIsUpdatingRisk(true);
            try {
              await updateProfile({ riskTolerance } as any);
              setMessage('Risk tolerance updated successfully');
            } catch (err: any) {
              setError(err.message || 'Failed to update risk tolerance');
            } finally {
              setIsUpdatingRisk(false);
            }
          }}
          disabled={isUpdatingRisk || riskTolerance === (user?.riskTolerance || 'all')}
          style={{
            padding: '0.5rem 1rem',
            backgroundColor: isUpdatingRisk || riskTolerance === (user?.riskTolerance || 'all') ? '#475569' : '#22c55e',
            color: 'white',
            border: 'none',
            borderRadius: '4px',
            cursor: isUpdatingRisk || riskTolerance === (user?.riskTolerance || 'all') ? 'not-allowed' : 'pointer'
          }}
        >
          {isUpdatingRisk ? 'Updating...' : 'Save Preference'}
        </button>
      </div>

      {/* Email Subscription Management */}
      <div className="subscription-section" style={{ marginBottom: '2rem', padding: '1rem', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '8px', background: 'rgba(255,255,255,0.05)' }}>
        <h2 style={{ color: '#ffffff' }}>Notification Preferences</h2>

        {/* Notches daily email digest — independent of stock daily report */}
        <div style={{ marginBottom: '1rem' }}>
          <label style={{ display: 'flex', alignItems: 'center', marginBottom: '0.5rem' }}>
            <input
              type="checkbox"
              checked={subscriptionForm.emailNotches}
              onChange={(e) => setSubscriptionForm({ ...subscriptionForm, emailNotches: e.target.checked })}
              disabled={!(user as any)?.notchesEnabled}
              style={{ marginRight: '0.5rem' }}
            />
            <div>
              <strong style={{ color: '#ffffff' }}>Notches Daily Email Digest</strong>
              <div style={{ fontSize: '0.9em', color: '#94a3b8' }}>End-of-day branded email with top WFS picks per preset, convergence scores (WFS / Claude / ChatGPT), and skew + macro context — one per symbol (SPX / SPY / ES / MES) you're subscribed to.</div>
              {!(user as any)?.notchesEnabled && (
                <div style={{ fontSize: '0.8em', color: '#f59e0b' }}>Requires Notches access</div>
              )}
            </div>
          </label>
        </div>

        {subscriptionForm.emailNotches && (
          <div style={{ marginTop: '1rem', padding: '1rem', backgroundColor: 'rgba(251,191,36,0.15)', border: '1px solid rgba(251,191,36,0.3)', borderRadius: '4px' }}>
            <h4 style={{ margin: '0 0 0.5rem 0', color: '#fbbf24' }}>Disclaimer Required</h4>
            <div style={{ fontSize: '0.8em', color: '#fbbf24', marginBottom: '1rem' }}>
              <p><strong>FOR INFORMATIONAL PURPOSES ONLY - NOT INVESTMENT ADVICE</strong></p>
              <p>This analysis is for informational and educational purposes only. Trading involves substantial risk of loss. We make no representations regarding accuracy. Consult qualified financial professionals before making investment decisions.</p>
            </div>
            <label style={{ display: 'flex', alignItems: 'flex-start' }}>
              <input
                type="checkbox"
                checked={subscriptionForm.disclaimerAccepted}
                onChange={(e) => setSubscriptionForm({ ...subscriptionForm, disclaimerAccepted: e.target.checked })}
                style={{ marginRight: '0.5rem', marginTop: '0.2rem' }}
              />
              <span style={{ fontSize: '0.9em', color: '#fbbf24' }}>
                I have read, understood, and agree to this disclaimer. I acknowledge that I am using this service at my own risk.
              </span>
            </label>
          </div>
        )}

        <button
          onClick={handleSubscriptionUpdate}
          disabled={isUpdatingSubscriptions || (subscriptionForm.emailNotches && !subscriptionForm.disclaimerAccepted)}
          style={{
            marginTop: '1rem',
            padding: '0.5rem 1rem',
            backgroundColor: isUpdatingSubscriptions ? '#475569' : '#22c55e',
            color: 'white',
            border: 'none',
            borderRadius: '4px',
            cursor: isUpdatingSubscriptions ? 'not-allowed' : 'pointer'
          }}
        >
          {isUpdatingSubscriptions ? 'Updating...' : 'Update Subscriptions'}
        </button>
      </div>

      {/* SMS Notifications Section */}
      <div className="sms-section" style={{ marginBottom: '2rem', padding: '1rem', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '8px', background: 'rgba(255,255,255,0.05)' }}>
        <h2 style={{ color: '#ffffff' }}>SMS Notifications</h2>
        {!['basic', 'premier', 'premium', 'pro'].includes(user.effectiveTier) ? (
          <p style={{ color: '#f59e0b', fontSize: '0.9em' }}>
            SMS notifications require a paid subscription.
          </p>
        ) : (
        <>
        <p style={{ color: '#94a3b8', fontSize: '0.9em', marginBottom: '1rem' }}>
          Receive text message alerts for research and trade executions. Verify your phone number to get started.
        </p>

        {/* Phone Verification */}
        {phoneStep === 'verified' || user?.phoneVerified ? (
          <div style={{ marginBottom: '1rem', padding: '0.75rem', backgroundColor: 'rgba(34,197,94,0.1)', border: '1px solid rgba(34,197,94,0.2)', borderRadius: '4px' }}>
            <span style={{ color: '#4ade80', fontWeight: 'bold' }}>Phone Verified</span>
            <span style={{ color: '#94a3b8', marginLeft: '0.75rem' }}>
              {user?.phone ? `${user.phone.slice(0, 4)}****${user.phone.slice(-2)}` : ''}
            </span>
          </div>
        ) : phoneStep === 'otp_sent' ? (
          <div style={{ marginBottom: '1rem' }}>
            <label style={{ display: 'block', color: '#cbd5e1', marginBottom: '0.5rem', fontWeight: 'bold' }}>Enter Verification Code</label>
            <div style={{ display: 'flex', gap: '0.5rem' }}>
              <input
                type="text"
                value={otpInput}
                onChange={(e) => setOtpInput(e.target.value.replace(/\D/g, '').slice(0, 6))}
                placeholder="6-digit code"
                maxLength={6}
                style={{
                  flex: 1, padding: '0.5rem',
                  border: '1px solid rgba(255,255,255,0.2)', borderRadius: '4px',
                  background: 'rgba(255,255,255,0.1)', color: '#ffffff', boxSizing: 'border-box'
                }}
              />
              <button
                onClick={async () => {
                  setError(null); setMessage(null);
                  setIsVerifyingOtp(true);
                  try {
                    await authService.verifyPhoneOtp(otpInput, token!);
                    setPhoneStep('verified');
                    setMessage('Phone number verified successfully');
                    // Reload user to get updated phoneVerified
                    await updateProfile({} as any);
                  } catch (err: any) {
                    setError(err.response?.data?.error || err.message || 'Verification failed');
                  } finally {
                    setIsVerifyingOtp(false);
                  }
                }}
                disabled={isVerifyingOtp || otpInput.length !== 6}
                style={{
                  padding: '0.5rem 1rem', backgroundColor: isVerifyingOtp || otpInput.length !== 6 ? '#475569' : '#22c55e',
                  color: 'white', border: 'none', borderRadius: '4px',
                  cursor: isVerifyingOtp || otpInput.length !== 6 ? 'not-allowed' : 'pointer'
                }}
              >
                {isVerifyingOtp ? 'Verifying...' : 'Verify'}
              </button>
            </div>
            <p style={{ color: '#64748b', fontSize: '0.8rem', marginTop: '0.5rem' }}>
              Code expires in 15 minutes. <button
                onClick={() => setPhoneStep('idle')}
                style={{ background: 'none', border: 'none', color: '#60a5fa', cursor: 'pointer', fontSize: '0.8rem', padding: 0 }}
              >Try a different number</button>
            </p>
          </div>
        ) : (
          <div style={{ marginBottom: '1rem' }}>
            <label style={{ display: 'block', color: '#cbd5e1', marginBottom: '0.5rem', fontWeight: 'bold' }}>Phone Number</label>
            <div style={{ display: 'flex', gap: '0.5rem' }}>
              <input
                type="tel"
                value={phoneInput}
                onChange={(e) => setPhoneInput(e.target.value)}
                placeholder="+12125551234"
                style={{
                  flex: 1, padding: '0.5rem',
                  border: '1px solid rgba(255,255,255,0.2)', borderRadius: '4px',
                  background: 'rgba(255,255,255,0.1)', color: '#ffffff', boxSizing: 'border-box'
                }}
              />
              <button
                onClick={async () => {
                  setError(null); setMessage(null);
                  if (!/^\+[1-9]\d{6,14}$/.test(phoneInput)) {
                    setError('Enter a valid phone number in E.164 format (e.g., +12125551234)');
                    return;
                  }
                  setIsSendingOtp(true);
                  try {
                    await authService.sendPhoneOtp(phoneInput, token!);
                    setPhoneStep('otp_sent');
                    setMessage('Verification code sent to your phone');
                  } catch (err: any) {
                    setError(err.response?.data?.error || err.message || 'Failed to send code');
                  } finally {
                    setIsSendingOtp(false);
                  }
                }}
                disabled={isSendingOtp || !phoneInput}
                style={{
                  padding: '0.5rem 1rem', backgroundColor: isSendingOtp || !phoneInput ? '#475569' : '#3b82f6',
                  color: 'white', border: 'none', borderRadius: '4px',
                  cursor: isSendingOtp || !phoneInput ? 'not-allowed' : 'pointer'
                }}
              >
                {isSendingOtp ? 'Sending...' : 'Send Code'}
              </button>
            </div>
            <p style={{ color: '#64748b', fontSize: '0.8rem', marginTop: '0.5rem' }}>
              US/Canada numbers: +1 followed by 10 digits. Standard SMS rates apply.
            </p>
          </div>
        )}

        {/* SMS Preference Toggles — only if phone is verified */}
        {(phoneStep === 'verified' || user?.phoneVerified) && (
          <div style={{ marginTop: '1rem' }}>
            {/* Sender identification — sets user expectations */}
            <div style={{ marginBottom: '1rem', padding: '0.75rem', background: 'rgba(59,130,246,0.08)', border: '1px solid rgba(59,130,246,0.25)', borderRadius: 6, fontSize: '0.85em', color: '#cbd5e1' }}>
              <div style={{ color: '#93c5fd', fontWeight: 600, marginBottom: 4 }}>
                Alerts will come from +1 844-417-1647 (Wealth Financial Systems)
              </div>
              <div style={{ color: '#94a3b8', fontSize: '0.9em' }}>Sample message:</div>
              <pre style={{ margin: '4px 0 0', padding: '6px 8px', background: '#0f172a', border: '1px solid #1e293b', borderRadius: 4, color: '#cbd5e1', fontSize: '0.85em', whiteSpace: 'pre-wrap', fontFamily: 'inherit' }}>
{`Wealth Financial Systems
SPY: Sell 670 04-19 $2.80cr | 72% PoP | High conf
Reply STOP to opt out`}
              </pre>
              <div style={{ marginTop: 6, fontSize: '0.8em', color: '#64748b' }}>
                Reply STOP at any time to stop all SMS from this number.
              </div>
            </div>

            {/* Subscription-scoped SMS toggles.
                A toggle is shown ONLY if the user has the corresponding subscription.
                Hidden (not disabled) — per the rule that we never send alerts for
                products the user hasn't subscribed to. Don't surface upgrade
                hints here; that lives elsewhere. */}
            {(() => {
              const u = user as any;
              const hasPremier =
                // 'premium' is the legacy admin/friends-family value;
                // 'premier'/'pro' are the new paid tier values.
                ['premier', 'pro', 'premium'].includes(u?.subscriptionTier) ||
                u?.subscriptions?.premier?.status === 'active';
              const hasSpx = !!u?.spxSignalsEnabled;
              const hasSpy = !!u?.spySignalsEnabled;
              const hasEs = !!u?.esSignalsEnabled;
              const hasMes = !!u?.mesSignalsEnabled;
              const hasAutoTrader = !!u?.optionsAutoTradingEnabled;
              const anyAvailable = hasPremier || hasSpx || hasSpy || hasEs || hasMes || hasAutoTrader;

              if (!anyAvailable) {
                return (
                  <div style={{ padding: '0.75rem', background: 'rgba(245,158,11,0.08)', border: '1px solid rgba(245,158,11,0.25)', borderRadius: 6, fontSize: '0.9em', color: '#fcd34d' }}>
                    No SMS alerts available with your current subscriptions. Subscribe to Premier (stock + options research) or per-product feeds (SPX, SPY, ES, MES) to enable alerts.
                  </div>
                );
              }

              return <>
                {/* Stock Signals — Premier-gated */}
                {hasPremier && (
                  <div style={{ marginBottom: '0.75rem' }}>
                    <label style={{ display: 'flex', alignItems: 'center' }}>
                      <input
                        type="checkbox"
                        checked={smsPrefs.smsStockSignals}
                        onChange={(e) => setSmsPrefs({ ...smsPrefs, smsStockSignals: e.target.checked })}
                        style={{ marginRight: '0.5rem' }}
                      />
                      <div>
                        <strong style={{ color: '#ffffff' }}>Stock Research Alerts</strong>
                        <div style={{ fontSize: '0.85em', color: '#94a3b8' }}>New stock picks and close notifications</div>
                      </div>
                    </label>
                  </div>
                )}

                {/* SPX */}
                {hasSpx && (
                  <div style={{ marginBottom: '0.75rem' }}>
                    <label style={{ display: 'flex', alignItems: 'center' }}>
                      <input
                        type="checkbox"
                        checked={smsPrefs.smsSpxSignals}
                        onChange={(e) => setSmsPrefs({ ...smsPrefs, smsSpxSignals: e.target.checked })}
                        style={{ marginRight: '0.5rem' }}
                      />
                      <div>
                        <strong style={{ color: '#ffffff' }}>SPX Research Alerts</strong>
                        <div style={{ fontSize: '0.85em', color: '#94a3b8' }}>SPX options research notifications</div>
                      </div>
                    </label>
                  </div>
                )}

                {/* SPY */}
                {hasSpy && (
                  <div style={{ marginBottom: '0.75rem' }}>
                    <label style={{ display: 'flex', alignItems: 'center' }}>
                      <input
                        type="checkbox"
                        checked={smsPrefs.smsSpySignals}
                        onChange={(e) => setSmsPrefs({ ...smsPrefs, smsSpySignals: e.target.checked })}
                        style={{ marginRight: '0.5rem' }}
                      />
                      <div>
                        <strong style={{ color: '#ffffff' }}>SPY Research Alerts</strong>
                        <div style={{ fontSize: '0.85em', color: '#94a3b8' }}>SPY options research notifications</div>
                      </div>
                    </label>
                  </div>
                )}

                {/* ES */}
                {hasEs && (
                  <div style={{ marginBottom: '0.75rem' }}>
                    <label style={{ display: 'flex', alignItems: 'center' }}>
                      <input
                        type="checkbox"
                        checked={smsPrefs.smsEsSignals}
                        onChange={(e) => setSmsPrefs({ ...smsPrefs, smsEsSignals: e.target.checked })}
                        style={{ marginRight: '0.5rem' }}
                      />
                      <div>
                        <strong style={{ color: '#ffffff' }}>ES Research Alerts</strong>
                        <div style={{ fontSize: '0.85em', color: '#94a3b8' }}>ES futures options research notifications</div>
                      </div>
                    </label>
                  </div>
                )}

                {/* MES */}
                {hasMes && (
                  <div style={{ marginBottom: '0.75rem' }}>
                    <label style={{ display: 'flex', alignItems: 'center' }}>
                      <input
                        type="checkbox"
                        checked={smsPrefs.smsMesSignals}
                        onChange={(e) => setSmsPrefs({ ...smsPrefs, smsMesSignals: e.target.checked })}
                        style={{ marginRight: '0.5rem' }}
                      />
                      <div>
                        <strong style={{ color: '#ffffff' }}>MES Research Alerts</strong>
                        <div style={{ fontSize: '0.85em', color: '#94a3b8' }}>MES futures options research notifications</div>
                      </div>
                    </label>
                  </div>
                )}

                {/* Options Trade Alerts (auto-trader) */}
                {hasAutoTrader && (
                  <div style={{ marginBottom: '0.75rem' }}>
                    <label style={{ display: 'flex', alignItems: 'center' }}>
                      <input
                        type="checkbox"
                        checked={smsPrefs.smsOptionsTrades}
                        onChange={(e) => setSmsPrefs({ ...smsPrefs, smsOptionsTrades: e.target.checked })}
                        style={{ marginRight: '0.5rem' }}
                      />
                      <div>
                        <strong style={{ color: '#ffffff' }}>Options Trade Alerts</strong>
                        <div style={{ fontSize: '0.85em', color: '#94a3b8' }}>Auto-trader open/close notifications (live trades only)</div>
                      </div>
                    </label>
                  </div>
                )}

                {/* Notches Alerts — gated on notchesEnabled flag */}
                {(user as any)?.notchesEnabled && (
                  <div style={{ marginBottom: '0.75rem' }}>
                    <label style={{ display: 'flex', alignItems: 'center' }}>
                      <input
                        type="checkbox"
                        checked={smsPrefs.smsNotchesEnabled}
                        onChange={(e) => setSmsPrefs({ ...smsPrefs, smsNotchesEnabled: e.target.checked })}
                        style={{ marginRight: '0.5rem' }}
                      />
                      <div>
                        <strong style={{ color: '#ffffff' }}>Notches Alerts</strong>
                        <div style={{ fontSize: '0.85em', color: '#94a3b8' }}>Per-snapshot SMS with the top WFS pick under your selected preset (one per producer run for each subscribed symbol).</div>
                      </div>
                    </label>
                  </div>
                )}
              </>;
            })()}

            <button
              onClick={async () => {
                setError(null); setMessage(null);
                setIsUpdatingSms(true);
                try {
                  await updateProfile(smsPrefs as any);
                  setMessage('SMS preferences updated successfully');
                } catch (err: any) {
                  setError(err.response?.data?.error || err.message || 'Failed to update SMS preferences');
                } finally {
                  setIsUpdatingSms(false);
                }
              }}
              disabled={isUpdatingSms}
              style={{
                padding: '0.5rem 1rem',
                backgroundColor: isUpdatingSms ? '#475569' : '#22c55e',
                color: 'white', border: 'none', borderRadius: '4px',
                cursor: isUpdatingSms ? 'not-allowed' : 'pointer'
              }}
            >
              {isUpdatingSms ? 'Saving...' : 'Save SMS Preferences'}
            </button>
          </div>
        )}
        </>
        )}
      </div>

      {/* Trading Snapshot — Daily Email Report (admin-controlled) */}
      {(user as any).tradingSnapshotEnabled !== undefined && (
        <div className="snapshot-section" style={{ marginBottom: '2rem', padding: '1rem', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '8px', background: 'rgba(255,255,255,0.05)' }}>
          <h2 style={{ color: '#ffffff' }}>Trading Snapshot</h2>
          <p style={{ color: '#94a3b8', fontSize: '0.9em', marginBottom: '1rem' }}>
            Daily market report emailed at 4:30 PM ET on trading days. Includes the day's best put-selling opportunities across SPX, SPY, ES, and MES with credit, buffer, and probability of profit.
          </p>
          <label style={{ display: 'flex', alignItems: 'center' }}>
            <input
              type="checkbox"
              checked={(user as any).tradingSnapshotEnabled || false}
              onChange={async (e) => {
                setError(null); setMessage(null);
                try {
                  await updateProfile({ tradingSnapshotEnabled: e.target.checked } as any);
                  setMessage(e.target.checked ? 'Subscribed to Trading Snapshot' : 'Unsubscribed from Trading Snapshot');
                } catch (err: any) {
                  setError(err.response?.data?.error || err.message || 'Failed to update');
                }
              }}
              style={{ marginRight: '0.5rem' }}
            />
            <div>
              <strong style={{ color: '#ffffff' }}>Email me the Trading Snapshot</strong>
              <div style={{ fontSize: '0.85em', color: '#94a3b8' }}>One email per trading day, sent after market close.</div>
            </div>
          </label>
        </div>
      )}

      {/* Brokerage Connection Section */}
      {(user.indexSignalsEnabled || (user as any).spySignalsEnabled || (user as any).esSignalsEnabled || (user as any).mesSignalsEnabled) && (
        <div className="tastytrade-section" style={{ marginBottom: '2rem', padding: '1rem', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '8px', background: 'rgba(255,255,255,0.05)' }}>
          <h2 style={{ color: '#ffffff', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            TastyTrade API Connection
            {user.tastyTradeConnected && (
              <span style={{ fontSize: '0.75rem', fontWeight: 600, padding: '0.2rem 0.6rem', borderRadius: '9999px', background: 'rgba(34,197,94,0.15)', color: '#4ade80' }}>
                Connected
              </span>
            )}
          </h2>

          {user.tastyTradeConnected ? (
            <>
              <div style={{ marginBottom: '1rem' }}>
                {/* Show all connections if multiple exist */}
                {(user as any).tastyTradeConnections?.length > 0 ? (
                  <div style={{ marginBottom: '0.5rem' }}>
                    <strong style={{ color: '#cbd5e1', fontSize: '0.9em' }}>Connected Accounts:</strong>
                    {(user as any).tastyTradeConnections.map((c: any, i: number) => (
                      <div key={c.connectionId || i} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginTop: '0.4rem', padding: '0.4rem 0.6rem', background: 'rgba(255,255,255,0.03)', borderRadius: 4 }}>
                        <span style={{ color: '#4ade80', fontSize: '0.7rem' }}>●</span>
                        <span style={{ color: '#f9fafb', fontWeight: 600, fontSize: '0.9em' }}>{c.label || `Account ${i + 1}`}</span>
                        <span style={{ color: '#9ca3af', fontSize: '0.85em' }}>{c.accountNumber}</span>
                        <button
                          onClick={async () => {
                            if (!c.connectionId) return;
                            if (!window.confirm(`Disconnect "${c.label || c.accountNumber}"?\n\nAny engine still pointing at this connection will be blocked from deletion. Re-point or delete those engines first.`)) return;
                            setError(null); setMessage(null);
                            try {
                              await authService.deleteTastyTradeCredentials(token!, { connectionId: c.connectionId });
                              setMessage(`Disconnected ${c.label || c.accountNumber}`);
                              setTimeout(() => window.location.reload(), 1200);
                            } catch (err: any) {
                              if (err?.response?.status === 409) {
                                const data = err.response.data || {};
                                const blockerList = (data.blockers || []).map((b: any) =>
                                  `  • ${b.name} [${b.brokerType}, ${b.status}${b.paperMode ? ', paper' : ', LIVE'}] acct ${b.accountNumber || '—'}`,
                                ).join('\n');
                                setError(`${data.error}\n\nBlocking engines:\n${blockerList}`);
                              } else {
                                setError(err?.response?.data?.error || err.message || 'Failed to disconnect');
                              }
                            }
                          }}
                          style={{
                            marginLeft: 'auto', padding: '0.25rem 0.6rem',
                            background: 'rgba(239,68,68,0.15)', color: '#f87171',
                            border: '1px solid rgba(239,68,68,0.3)', borderRadius: 4,
                            fontSize: '0.75em', cursor: 'pointer', fontWeight: 600,
                          }}
                        >Disconnect</button>
                      </div>
                    ))}
                  </div>
                ) : (
                <p style={{ color: '#94a3b8', marginBottom: '0.5rem' }}>
                  <strong style={{ color: '#cbd5e1' }}>Account:</strong>{' '}
                  {user.tastyTradeAccountNumber
                    ? `****${user.tastyTradeAccountNumber.slice(-4)}`
                    : 'Connected'}
                </p>
                )}
                {ttAccountData && (
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.5rem 2rem', marginTop: '0.75rem' }}>
                    <p style={{ color: '#94a3b8', margin: 0 }}>
                      <strong style={{ color: '#cbd5e1' }}>Options Buying Power:</strong>{' '}
                      ${ttAccountData.derivativeBuyingPower?.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 }) || '—'}
                    </p>
                    <p style={{ color: '#94a3b8', margin: 0 }}>
                      <strong style={{ color: '#cbd5e1' }}>Equity Buying Power:</strong>{' '}
                      ${ttAccountData.equityBuyingPower?.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 }) || '—'}
                    </p>
                    <p style={{ color: '#94a3b8', margin: 0 }}>
                      <strong style={{ color: '#cbd5e1' }}>Net Liquidating Value:</strong>{' '}
                      ${ttAccountData.netLiquidatingValue?.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 }) || '—'}
                    </p>
                    <p style={{ color: '#94a3b8', margin: 0 }}>
                      <strong style={{ color: '#cbd5e1' }}>Cash Balance:</strong>{' '}
                      ${ttAccountData.cashBalance?.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 }) || '—'}
                    </p>
                    <p style={{ color: '#94a3b8', margin: 0 }}>
                      <strong style={{ color: '#cbd5e1' }}>Open Positions:</strong>{' '}
                      {ttAccountData.positionsCount ?? '—'}
                    </p>
                  </div>
                )}
              </div>
              <div style={{ display: 'flex', gap: '0.75rem' }}>
                <button
                  onClick={async () => {
                    setError(null); setMessage(null);
                    setIsLoadingTTAccount(true);
                    try {
                      const data = await authService.getTastyTradeAccount(token!);
                      if (data.error) {
                        setError(data.error);
                        setTtAccountData(null);
                      } else {
                        setTtAccountData(data);
                        setMessage('Account data refreshed');
                      }
                    } catch (err: any) {
                      const errMsg = err.response?.data?.error || err.message || 'Failed to load account data';
                      setError(errMsg);
                      if (errMsg.includes('expired')) setTtAccountData(null);
                    } finally {
                      setIsLoadingTTAccount(false);
                    }
                  }}
                  disabled={isLoadingTTAccount}
                  style={{
                    padding: '0.5rem 1rem', backgroundColor: isLoadingTTAccount ? '#475569' : '#3b82f6',
                    color: 'white', border: 'none', borderRadius: '4px',
                    cursor: isLoadingTTAccount ? 'not-allowed' : 'pointer'
                  }}
                >
                  {isLoadingTTAccount ? 'Loading...' : 'Refresh Account Data'}
                </button>
                {/* Legacy "Disconnect (all)" button — only shown when no
                    multi-connection array is in use, so it's still possible to
                    detach a pre-multi-connection legacy account. With multiple
                    connections, use the per-row Disconnect buttons above. */}
                {!((user as any).tastyTradeConnections?.length > 0) && (
                  <button
                    onClick={async () => {
                      if (!window.confirm('Disconnect TastyTrade?\n\nAny engine still pointing at this connection will be blocked from deletion. Re-point or delete those engines first.')) return;
                      setError(null); setMessage(null);
                      setIsDisconnectingTT(true);
                      try {
                        await authService.deleteTastyTradeCredentials(token!);
                        setMessage('TastyTrade account disconnected');
                        setTtAccountData(null);
                        setTimeout(() => window.location.reload(), 1500);
                      } catch (err: any) {
                        if (err?.response?.status === 409) {
                          const data = err.response.data || {};
                          const blockerList = (data.blockers || []).map((b: any) =>
                            `  • ${b.name} [${b.brokerType}, ${b.status}${b.paperMode ? ', paper' : ', LIVE'}] acct ${b.accountNumber || '—'}`,
                          ).join('\n');
                          setError(`${data.error}\n\nBlocking engines:\n${blockerList}`);
                        } else {
                          setError(err?.response?.data?.error || err.message || 'Failed to disconnect');
                        }
                      } finally {
                        setIsDisconnectingTT(false);
                      }
                    }}
                    disabled={isDisconnectingTT}
                    style={{
                      padding: '0.5rem 1rem', backgroundColor: 'rgba(239,68,68,0.15)',
                      color: '#f87171', border: '1px solid rgba(239,68,68,0.3)', borderRadius: '4px',
                      cursor: isDisconnectingTT ? 'not-allowed' : 'pointer', fontWeight: 600
                    }}
                  >
                    {isDisconnectingTT ? 'Disconnecting...' : 'Disconnect'}
                  </button>
                )}
              </div>

              {/* Add Another Connection */}
              <div style={{ marginTop: '1rem', borderTop: '1px solid rgba(255,255,255,0.1)', paddingTop: '1rem' }}>
                <button
                  onClick={() => setShowAddConnection(!showAddConnection)}
                  style={{ background: 'none', border: 'none', color: '#60a5fa', cursor: 'pointer', fontSize: '0.9em', padding: 0 }}
                >
                  {showAddConnection ? '▾ Add Another Connection' : '▸ Add Another Connection'}
                </button>
                {showAddConnection && (
                  <div style={{ marginTop: '0.75rem' }}>
                    <div style={{ marginBottom: '0.75rem' }}>
                      <label style={{ display: 'block', color: '#cbd5e1', marginBottom: '0.25rem', fontWeight: 'bold', fontSize: '0.9em' }}>Refresh Token</label>
                      <input type="password" value={ttRefreshToken} onChange={(e) => setTtRefreshToken(e.target.value)} placeholder="Paste your refresh token" style={{ width: '100%', padding: '0.5rem', border: '1px solid rgba(255,255,255,0.2)', borderRadius: '4px', background: 'rgba(255,255,255,0.1)', color: '#ffffff', boxSizing: 'border-box' }} />
                    </div>
                    <div style={{ marginBottom: '0.75rem' }}>
                      <label style={{ display: 'block', color: '#cbd5e1', marginBottom: '0.25rem', fontWeight: 'bold', fontSize: '0.9em' }}>Client Secret</label>
                      <input type="password" value={ttClientSecret} onChange={(e) => setTtClientSecret(e.target.value)} placeholder="Paste your client secret" style={{ width: '100%', padding: '0.5rem', border: '1px solid rgba(255,255,255,0.2)', borderRadius: '4px', background: 'rgba(255,255,255,0.1)', color: '#ffffff', boxSizing: 'border-box' }} />
                    </div>
                    <button
                      onClick={async () => {
                        setError(null); setMessage(null);
                        if (!ttRefreshToken.trim() || !ttClientSecret.trim()) { setError('Both refresh token and client secret are required'); return; }
                        setIsConnectingTT(true);
                        try {
                          const result = await authService.saveTastyTradeCredentials(ttRefreshToken.trim(), ttClientSecret.trim(), token!);
                          if (result.chooseAccount && result.accounts?.length > 1) {
                            setTtAccounts(result.accounts);
                            setMessage('Credentials verified. Please select an account below.');
                          } else {
                            setMessage(`Additional TastyTrade account connected (****${result.accountNumber.slice(-4)})`);
                            setTtRefreshToken(''); setTtClientSecret(''); setShowAddConnection(false);
                            setTimeout(() => window.location.reload(), 1500);
                          }
                        } catch (err: any) { setError(err.response?.data?.error || err.message || 'Failed to connect'); }
                        finally { setIsConnectingTT(false); }
                      }}
                      disabled={isConnectingTT}
                      style={{ padding: '0.5rem 1.25rem', backgroundColor: isConnectingTT ? '#475569' : '#22c55e', color: 'white', border: 'none', borderRadius: '4px', cursor: isConnectingTT ? 'not-allowed' : 'pointer', fontWeight: 600 }}
                    >
                      {isConnectingTT ? 'Connecting...' : 'Connect & Test'}
                    </button>

                    {/* Account picker for multi-account logins */}
                    {ttAccounts.length > 1 && (
                      <div style={{ marginTop: '1rem', padding: '1rem', backgroundColor: 'rgba(59,130,246,0.08)', border: '1px solid rgba(59,130,246,0.2)', borderRadius: '4px' }}>
                        <label style={{ display: 'block', color: '#cbd5e1', marginBottom: '0.5rem', fontWeight: 'bold', fontSize: '0.9em' }}>
                          Multiple accounts found — select one:
                        </label>
                        {ttAccounts.map((acct: any) => (
                          <label key={acct.accountNumber} style={{ display: 'flex', alignItems: 'center', marginBottom: '0.5rem', cursor: 'pointer' }}>
                            <input type="radio" name="ttAccountAdd" value={acct.accountNumber} checked={ttSelectedAccount === acct.accountNumber} onChange={() => setTtSelectedAccount(acct.accountNumber)} style={{ marginRight: '0.5rem' }} />
                            <div>
                              <strong style={{ color: '#ffffff' }}>****{acct.accountNumber.slice(-4)}</strong>
                              <span style={{ color: '#94a3b8', marginLeft: '0.5rem', fontSize: '0.85em' }}>{acct.nickname || acct.accountType || ''} {acct.marginOrCash ? `(${acct.marginOrCash})` : ''}</span>
                            </div>
                          </label>
                        ))}
                        <button
                          onClick={async () => {
                            if (!ttSelectedAccount) { setError('Please select an account'); return; }
                            setError(null); setMessage(null); setIsConnectingTT(true);
                            try {
                              const result = await authService.saveTastyTradeCredentials(ttRefreshToken.trim(), ttClientSecret.trim(), token!, ttSelectedAccount);
                              setMessage(`Additional TastyTrade account connected (****${result.accountNumber.slice(-4)})`);
                              setTtRefreshToken(''); setTtClientSecret(''); setTtAccounts([]); setTtSelectedAccount(''); setShowAddConnection(false);
                              setTimeout(() => window.location.reload(), 1500);
                            } catch (err: any) { setError(err.response?.data?.error || err.message || 'Failed to connect'); }
                            finally { setIsConnectingTT(false); }
                          }}
                          disabled={isConnectingTT || !ttSelectedAccount}
                          style={{ padding: '0.5rem 1.25rem', marginTop: '0.5rem', backgroundColor: isConnectingTT || !ttSelectedAccount ? '#475569' : '#22c55e', color: 'white', border: 'none', borderRadius: '4px', cursor: isConnectingTT || !ttSelectedAccount ? 'not-allowed' : 'pointer', fontWeight: 600 }}
                        >
                          {isConnectingTT ? 'Connecting...' : 'Connect Selected Account'}
                        </button>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </>
          ) : (
            <>
              <button
                onClick={() => setShowTTSetup(!showTTSetup)}
                style={{
                  background: 'none', border: 'none', color: '#60a5fa', cursor: 'pointer',
                  fontSize: '0.9em', padding: 0, marginBottom: '1rem', display: 'block'
                }}
              >
                {showTTSetup ? '▾ How to set up' : '▸ How to set up'}
              </button>

              {showTTSetup && (
                <div style={{ marginBottom: '1rem', padding: '1rem', backgroundColor: 'rgba(59,130,246,0.08)', border: '1px solid rgba(59,130,246,0.2)', borderRadius: '4px' }}>
                  <ol style={{ color: '#94a3b8', margin: '0 0 1rem 0', paddingLeft: '1.25rem', lineHeight: '1.8' }}>
                    <li>Log in to <strong style={{ color: '#cbd5e1' }}>tastytrade.com</strong></li>
                    <li>Click <strong style={{ color: '#cbd5e1' }}>Manage</strong> &gt; <strong style={{ color: '#cbd5e1' }}>Profile</strong> &gt; <strong style={{ color: '#cbd5e1' }}>API</strong> tab</li>
                    <li>Click <strong style={{ color: '#cbd5e1' }}>OAuth Application</strong> &gt; <strong style={{ color: '#cbd5e1' }}>Create Application</strong></li>
                    <li>Save the <strong style={{ color: '#cbd5e1' }}>Client Secret</strong> shown (you won't see it again)</li>
                    <li>Click the <strong style={{ color: '#cbd5e1' }}>three dots (...)</strong> next to your app's Client ID &gt; <strong style={{ color: '#cbd5e1' }}>Create Grant</strong></li>
                    <li>Select scopes: <strong style={{ color: '#cbd5e1' }}>Read</strong> and <strong style={{ color: '#cbd5e1' }}>Trade</strong> (not OpenAI)</li>
                    <li>Copy the <strong style={{ color: '#cbd5e1' }}>Refresh Token</strong> generated</li>
                    <li>Paste both below and click "Connect & Test"</li>
                  </ol>
                  <div style={{ fontSize: '0.85em', color: '#64748b', lineHeight: '1.6' }}>
                    <p style={{ margin: '0 0 0.25rem 0' }}><strong style={{ color: '#94a3b8' }}>What we access:</strong> Account balances and positions only.</p>
                    <p style={{ margin: '0 0 0.25rem 0' }}>We do NOT place trades or modify your account.</p>
                    <p style={{ margin: '0 0 0.25rem 0' }}>Your credentials are encrypted in AWS Secrets Manager.</p>
                    <p style={{ margin: 0 }}>You can disconnect at any time.</p>
                  </div>
                </div>
              )}

              <div style={{ marginBottom: '0.75rem' }}>
                <label style={{ display: 'block', color: '#cbd5e1', marginBottom: '0.25rem', fontWeight: 'bold', fontSize: '0.9em' }}>Refresh Token</label>
                <input
                  type="password"
                  value={ttRefreshToken}
                  onChange={(e) => setTtRefreshToken(e.target.value)}
                  placeholder="Paste your refresh token"
                  style={{
                    width: '100%', padding: '0.5rem',
                    border: '1px solid rgba(255,255,255,0.2)', borderRadius: '4px',
                    background: 'rgba(255,255,255,0.1)', color: '#ffffff', boxSizing: 'border-box'
                  }}
                />
              </div>
              <div style={{ marginBottom: '1rem' }}>
                <label style={{ display: 'block', color: '#cbd5e1', marginBottom: '0.25rem', fontWeight: 'bold', fontSize: '0.9em' }}>Client Secret</label>
                <input
                  type="password"
                  value={ttClientSecret}
                  onChange={(e) => setTtClientSecret(e.target.value)}
                  placeholder="Paste your client secret"
                  style={{
                    width: '100%', padding: '0.5rem',
                    border: '1px solid rgba(255,255,255,0.2)', borderRadius: '4px',
                    background: 'rgba(255,255,255,0.1)', color: '#ffffff', boxSizing: 'border-box'
                  }}
                />
              </div>
              {/* Account picker — shown when multiple accounts found */}
              {ttAccounts.length > 1 && (
                <div style={{ marginBottom: '1rem', padding: '1rem', backgroundColor: 'rgba(59,130,246,0.08)', border: '1px solid rgba(59,130,246,0.2)', borderRadius: '4px' }}>
                  <label style={{ display: 'block', color: '#cbd5e1', marginBottom: '0.5rem', fontWeight: 'bold', fontSize: '0.9em' }}>
                    Multiple accounts found — select one:
                  </label>
                  {ttAccounts.map((acct: any) => (
                    <label key={acct.accountNumber} style={{ display: 'flex', alignItems: 'center', marginBottom: '0.5rem', cursor: 'pointer' }}>
                      <input
                        type="radio"
                        name="ttAccount"
                        value={acct.accountNumber}
                        checked={ttSelectedAccount === acct.accountNumber}
                        onChange={() => setTtSelectedAccount(acct.accountNumber)}
                        style={{ marginRight: '0.5rem' }}
                      />
                      <div>
                        <strong style={{ color: '#ffffff' }}>****{acct.accountNumber.slice(-4)}</strong>
                        <span style={{ color: '#94a3b8', marginLeft: '0.5rem', fontSize: '0.85em' }}>
                          {acct.nickname || acct.accountType || ''} {acct.marginOrCash ? `(${acct.marginOrCash})` : ''}
                        </span>
                      </div>
                    </label>
                  ))}
                  <button
                    onClick={async () => {
                      if (!ttSelectedAccount) { setError('Please select an account'); return; }
                      setError(null); setMessage(null);
                      setIsConnectingTT(true);
                      try {
                        const result = await authService.saveTastyTradeCredentials(ttRefreshToken.trim(), ttClientSecret.trim(), token!, ttSelectedAccount);
                        setMessage(`TastyTrade account connected (****${result.accountNumber.slice(-4)})`);
                        setTtRefreshToken(''); setTtClientSecret(''); setTtAccounts([]); setTtSelectedAccount('');
                        setTimeout(() => window.location.reload(), 1500);
                      } catch (err: any) {
                        setError(err.response?.data?.error || err.message || 'Failed to connect');
                      } finally {
                        setIsConnectingTT(false);
                      }
                    }}
                    disabled={isConnectingTT || !ttSelectedAccount}
                    style={{
                      padding: '0.5rem 1.25rem', marginTop: '0.5rem',
                      backgroundColor: isConnectingTT || !ttSelectedAccount ? '#475569' : '#22c55e',
                      color: 'white', border: 'none', borderRadius: '4px',
                      cursor: isConnectingTT || !ttSelectedAccount ? 'not-allowed' : 'pointer', fontWeight: 600
                    }}
                  >
                    {isConnectingTT ? 'Connecting...' : 'Connect Selected Account'}
                  </button>
                </div>
              )}

              {ttAccounts.length === 0 && (
              <button
                onClick={async () => {
                  setError(null); setMessage(null);
                  if (!ttRefreshToken.trim() || !ttClientSecret.trim()) {
                    setError('Both refresh token and client secret are required');
                    return;
                  }
                  setIsConnectingTT(true);
                  try {
                    const result = await authService.saveTastyTradeCredentials(ttRefreshToken.trim(), ttClientSecret.trim(), token!);
                    if (result.chooseAccount && result.accounts?.length > 1) {
                      setTtAccounts(result.accounts);
                      setMessage('Credentials verified. Please select an account below.');
                    } else {
                      setMessage(`TastyTrade account connected (****${result.accountNumber.slice(-4)})`);
                      setTtRefreshToken(''); setTtClientSecret('');
                      setTimeout(() => window.location.reload(), 1500);
                    }
                  } catch (err: any) {
                    setError(err.response?.data?.error || err.message || 'Failed to connect');
                  } finally {
                    setIsConnectingTT(false);
                  }
                }}
                disabled={isConnectingTT || !ttRefreshToken.trim() || !ttClientSecret.trim()}
                style={{
                  padding: '0.5rem 1.25rem',
                  backgroundColor: isConnectingTT || !ttRefreshToken.trim() || !ttClientSecret.trim() ? '#475569' : '#22c55e',
                  color: 'white', border: 'none', borderRadius: '4px',
                  cursor: isConnectingTT || !ttRefreshToken.trim() || !ttClientSecret.trim() ? 'not-allowed' : 'pointer',
                  fontWeight: 600
                }}
              >
                {isConnectingTT ? 'Connecting...' : 'Connect & Test'}
              </button>
              )}
            </>
          )}
        </div>
      )}

      {/* Risk Engine Key Section (BB-only) */}
      {user.bbProgramEnabled === true && (
        <div className="risk-engine-key-section" style={{ marginBottom: '2rem', padding: '1rem', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '8px', background: 'rgba(255,255,255,0.05)' }}>
          <h2 style={{ color: '#ffffff' }}>Risk Engine Key</h2>
          <p style={{ color: '#cbd5e1', fontSize: '0.9rem', marginTop: '-0.25rem' }}>
            Manage your HMAC key for the deid-aws risk engine. Most users can ignore this &mdash; the engine auto-mints a key on first Analyze Risk run. Only needed if you plan to use the same key from a Claude Desktop deid-gateway client.
          </p>
          <EngineKeySettings token={token} />
        </div>
      )}

      {/* Experience & Risk Section */}
      <div className="experience-risk-section" style={{ marginBottom: '2rem', padding: '1rem', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '8px', background: 'rgba(255,255,255,0.05)' }}>
        <h2 style={{ color: '#ffffff' }}>Experience &amp; Risk</h2>
        <ExperienceAndRisk token={token} />
      </div>

      {/* Security Section */}
      <div className="password-section" style={{ marginBottom: '2rem', padding: '1rem', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '8px', background: 'rgba(255,255,255,0.05)' }}>
        <h2 style={{ color: '#ffffff' }}>Security</h2>
        {!isChangingPassword ? (
          <button
            onClick={() => setIsChangingPassword(true)}
            style={{
              padding: '0.5rem 1rem',
              backgroundColor: '#3b82f6',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer'
            }}
          >
            Change Password
          </button>
        ) : (
          <form onSubmit={handlePasswordChange}>
            <div style={{ marginBottom: '1rem' }}>
              <label style={{ color: '#cbd5e1' }}>Current Password:</label>
              <input
                type="password"
                value={passwordForm.currentPassword}
                onChange={(e) => setPasswordForm({ ...passwordForm, currentPassword: e.target.value })}
                required
                style={{
                  width: '100%',
                  padding: '0.5rem',
                  marginTop: '0.25rem',
                  border: '1px solid rgba(255,255,255,0.2)',
                  borderRadius: '4px',
                  background: 'rgba(255,255,255,0.1)',
                  color: '#ffffff',
                  boxSizing: 'border-box'
                }}
              />
            </div>
            <div style={{ marginBottom: '1rem' }}>
              <label style={{ color: '#cbd5e1' }}>New Password:</label>
              <input
                type="password"
                value={passwordForm.newPassword}
                onChange={(e) => setPasswordForm({ ...passwordForm, newPassword: e.target.value })}
                required
                minLength={8}
                style={{
                  width: '100%',
                  padding: '0.5rem',
                  marginTop: '0.25rem',
                  border: '1px solid rgba(255,255,255,0.2)',
                  borderRadius: '4px',
                  background: 'rgba(255,255,255,0.1)',
                  color: '#ffffff',
                  boxSizing: 'border-box'
                }}
              />
            </div>
            <div style={{ marginBottom: '1rem' }}>
              <label style={{ color: '#cbd5e1' }}>Confirm New Password:</label>
              <input
                type="password"
                value={passwordForm.confirmPassword}
                onChange={(e) => setPasswordForm({ ...passwordForm, confirmPassword: e.target.value })}
                required
                style={{
                  width: '100%',
                  padding: '0.5rem',
                  marginTop: '0.25rem',
                  border: '1px solid rgba(255,255,255,0.2)',
                  borderRadius: '4px',
                  background: 'rgba(255,255,255,0.1)',
                  color: '#ffffff',
                  boxSizing: 'border-box'
                }}
              />
            </div>
            <div style={{ display: 'flex', gap: '1rem' }}>
              <button
                type="submit"
                style={{
                  padding: '0.5rem 1rem',
                  backgroundColor: '#22c55e',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer'
                }}
              >
                Update Password
              </button>
              <button
                type="button"
                onClick={() => {
                  setIsChangingPassword(false);
                  setPasswordForm({ currentPassword: '', newPassword: '', confirmPassword: '' });
                  setError(null);
                  setMessage(null);
                }}
                style={{
                  padding: '0.5rem 1rem',
                  backgroundColor: 'rgba(255,255,255,0.1)',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer'
                }}
              >
                Cancel
              </button>
            </div>
          </form>
        )}

        {/* Two-Factor Authentication */}
        <div style={{ marginTop: '1.5rem', paddingTop: '1.5rem', borderTop: '1px solid rgba(255,255,255,0.08)' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '1rem' }}>
            <div>
              <h3 style={{ color: '#ffffff', margin: '0 0 0.25rem 0', fontSize: '1rem' }}>
                Two-Factor Authentication
                <span style={{
                  marginLeft: '0.75rem',
                  fontSize: '0.75rem',
                  fontWeight: 600,
                  padding: '0.2rem 0.6rem',
                  borderRadius: '9999px',
                  background: user?.twoFactorEnabled ? 'rgba(34,197,94,0.15)' : 'rgba(100,116,139,0.2)',
                  color: user?.twoFactorEnabled ? '#4ade80' : '#94a3b8',
                }}>
                  {user?.twoFactorEnabled ? 'Enabled' : 'Disabled'}
                </span>
              </h3>
              <p style={{ color: '#94a3b8', fontSize: '0.85em', margin: 0 }}>
                {user?.twoFactorEnabled
                  ? 'A verification code will be emailed to you each time you sign in.'
                  : 'Add an extra layer of security by requiring an email code on every login.'}
              </p>
            </div>
            <button
              onClick={async () => {
                setError(null);
                setMessage(null);
                setIsTogglingTwoFactor(true);
                try {
                  await updateProfile({ twoFactorEnabled: !user?.twoFactorEnabled } as any);
                  setMessage(user?.twoFactorEnabled ? '2FA has been disabled.' : '2FA has been enabled. You will receive a code by email on your next login.');
                } catch (err: any) {
                  setError(err.message || 'Failed to update 2FA setting');
                } finally {
                  setIsTogglingTwoFactor(false);
                }
              }}
              disabled={isTogglingTwoFactor}
              style={{
                padding: '0.5rem 1.25rem',
                backgroundColor: user?.twoFactorEnabled ? 'rgba(239,68,68,0.15)' : '#3b82f6',
                color: user?.twoFactorEnabled ? '#f87171' : 'white',
                border: user?.twoFactorEnabled ? '1px solid rgba(239,68,68,0.3)' : 'none',
                borderRadius: '4px',
                cursor: isTogglingTwoFactor ? 'not-allowed' : 'pointer',
                fontWeight: 600,
                fontSize: '0.875rem',
                whiteSpace: 'nowrap',
              }}
            >
              {isTogglingTwoFactor ? 'Saving...' : user?.twoFactorEnabled ? 'Disable 2FA' : 'Enable 2FA'}
            </button>
          </div>
        </div>
      </div>

      {/* Messages */}
      {message && (
        <div style={{ padding: '0.75rem', backgroundColor: 'rgba(34,197,94,0.15)', color: '#4ade80', border: '1px solid rgba(34,197,94,0.3)', borderRadius: '4px', marginBottom: '1rem' }}>
          {message}
        </div>
      )}
      {error && (
        <div style={{ padding: '0.75rem', backgroundColor: 'rgba(239,68,68,0.15)', color: '#f87171', border: '1px solid rgba(239,68,68,0.3)', borderRadius: '4px', marginBottom: '1rem', whiteSpace: 'pre-wrap' }}>
          {error}
        </div>
      )}

      </div>
    </div>
  );
};
